import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentType, type ElementType, forwardRef, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

const linkVariants = cva(
	[
		"group inline-flex items-center gap-1.5",
		"font-body font-normal",
		"cursor-pointer",
		"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
		"transition-colors duration-200",
		"[&_.link-label]:decoration-solid",
		"[&_.link-label]:[text-decoration-skip-ink:none]",
		"[&_.line]:block [&_.line]:h-px [&_.line]:w-0",
		"[&_.line]:transition-[width] [&_.line]:duration-300 [&_.line]:ease-reveal",
		"md:hover:[&_.line]:w-full",
	],
	{
		variants: {
			variant: {
				primary: ["text-text-dark-text-1"],
				secondary: ["text-text-dark-text-1"],
			},
			size: {
				small: "text-xs leading-[1.5]",
				medium: "text-sm leading-[1.5]",
				large: "text-base leading-[1.5]",
				xl: "text-lg leading-[1.5]",
			},
			light: {
				true: "[&_.line]:bg-white",
				false: "[&_.line]:bg-current",
			},
		},
		defaultVariants: {
			variant: "primary",
			size: "medium",
			light: false,
		},
	},
);

const iconSizes = cva("shrink-0", {
	variants: {
		size: {
			small: "size-3.5",
			medium: "size-6",
			large: "size-4",
			xl: "size-[1.125rem]",
		},
	},
	defaultVariants: {
		size: "medium",
	},
});

const numberSizes = cva("font-body font-normal tabular-nums lining-nums leading-[1.15]", {
	variants: {
		size: {
			small: "text-[0.484rem]",
			medium: "text-[0.564rem]",
			large: "text-[0.645rem]",
			xl: "text-[0.726rem]",
		},
	},
	defaultVariants: {
		size: "medium",
	},
});

type LinkVariants = VariantProps<typeof linkVariants>;

type SharedProps = LinkVariants & {
	children?: ReactNode;
	number?: number | string;
	leftIcon?: ComponentType<{ className?: string }>;
	rightIcon?: ComponentType<{ className?: string }>;
	className?: string;
	href?: string;
	target?: string;
	rel?: string;
	onClick?: () => void;
	unstyled?: boolean;
};

type LinkAsAnchor = SharedProps & {
	as?: "a";
};

type LinkAsComponent = SharedProps & {
	as: ElementType;
};

type Props = LinkAsAnchor | LinkAsComponent;

export const Link = forwardRef<HTMLElement, Props>((props, ref) => {
	const {
		children,
		number,
		size,
		variant,
		light,
		leftIcon: LeftIcon,
		rightIcon: RightIcon,
		className,
		href,
		target,
		rel,
		onClick,

		unstyled,
	} = props;

	const classes = twMerge(linkVariants({ size, variant, light }), className);
	const content = (
		<>
			{LeftIcon && <LeftIcon className={iconSizes({ size })} />}
			{unstyled ? (
				children
			) : (
				<div className="link-label">
					{children}
					<div className="line mt-[0.2em] h-1" />
				</div>
			)}
			{number != null && <sup className={numberSizes({ size })}>{number}</sup>}
			{RightIcon && (
				<div className={`${iconSizes({ size })} overflow-hidden`}>
					<div className="pointer-events-none flex -translate-x-full transition-transform duration-500 ease-reveal md:group-hover:translate-x-0">
						<RightIcon className={iconSizes({ size })} />
						<RightIcon className={iconSizes({ size })} />
					</div>
				</div>
			)}
		</>
	);

	const Component: ElementType = props.as && props.as !== "a" ? props.as : "a";

	return (
		<Component
			ref={ref}
			className={unstyled ? className : classes}
			href={href}
			target={target}
			rel={rel}
			onClick={onClick}
		>
			{content}
		</Component>
	);
});

Link.displayName = "Link";
